import { CloseOutlineIcon } from '@carboncopyco/icons';
import type { Workspace } from '@carboncopyco/types';
import { useState } from 'react';
import { object, string } from 'yup';

import {
  Badge,
  Button,
  Form,
  InputTextField,
  Spacer,
  TextCaption,
  TextSubHeader,
} from '@carboncopyco/ui';
import { useFlagsFallback } from '../../../hooks/useFlagsFallback';
import { WorkspaceSelectionField } from '../../Workspaces/WorkspaceSelectionField';

export const descriptionSchema = object({
  demoName: string().required('Required'),
  description: string(),
}).required();

interface SaveDemoProps {
  onCancel: () => void;
  onExit: () => void;
  publishDemo: (form: Record<string, string>) => any;
  demoName?: string;
  demoDescription: string;
  workspace: Workspace;
  editsNumber: number;
}

const SaveDemo = ({
  onCancel,
  onExit,
  publishDemo,
  demoName,
  demoDescription,
  workspace,
  editsNumber,
}: SaveDemoProps) => {
  const initialValues = {
    demoName: demoName || 'Untitled overlay',
    description: demoDescription,
    workspaceId: workspace.id,
  };
  const flags = useFlagsFallback();

  const [formValues, setFormValues] =
    useState<Record<string, string>>(initialValues);

  const handleSaveAndExit = async () => {
    await publishDemo(formValues);
    onExit();
  };

  return (
    <div className="w-ds-400">
      <Form
        enableReinitialize
        onSubmit={publishDemo}
        validationSchema={descriptionSchema}
        initialValues={initialValues}
        onValueChange={setFormValues}
      >
        <>
          <div className="flex items-center gap-2.5">
            <TextSubHeader
              color="text-ds-natural-600"
              label="Done editing?"
            />
            <div className="flex gap-1.5">
              <Badge
                label={String(editsNumber)}
                variant="primary"
                round
              />
              <TextCaption label="Edits" />
            </div>
            <div
              onClick={onCancel}
              className="cursor-pointer ml-auto"
            >
              <CloseOutlineIcon />
            </div>
          </div>
          <Spacer amount={3} />
          <InputTextField
            name="demoName"
            label="Demo name"
            required
          />
          <InputTextField
            label="Describe your changes"
            name="description"
            multiline
            inputSize="sm"
          />
          {workspace && (
            <div className="mt-2">
              <WorkspaceSelectionField
                workspaces={[workspace]}
                label="Save in..."
                name="workspaceId"
                width={400}
                disabled
              />
            </div>
          )}
          <Spacer amount={4} />
          <div className="w-full flex justify-end gap-2 ">
            {flags.desktopAppShowSaveAndExitButton && (
              <Button
                label="Save & Exit"
                secondary
                onClick={handleSaveAndExit}
              />
            )}
            <Button
              label="Save"
              submit
            />
          </div>
        </>
      </Form>
    </div>
  );
};

export default SaveDemo;
